import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type FileDownloadResponse,
  type SubjectFileItem,
  type SubjectFilesResponse,
  UPLOAD_EXTENSIONS,
  uploadError,
} from '@k-management/shared';
import { type ReactNode, useRef, useState } from 'react';
import { apiRequest, apiUpload } from '../lib/api.js';
import { formatFileSize, formatTimestamp } from '../lib/format.js';
import { ConfirmDialog } from './ConfirmDialog.js';

/** The files of a folder on SharePoint, through the API (docs/adr/ADR-009). */
export function useFolderFiles(base: string, queryKey: readonly unknown[], enabled: boolean) {
  return useQuery({
    queryKey,
    queryFn: () => apiRequest<SubjectFilesResponse>(base),
    enabled,
    retry: false,
  });
}

/**
 * A folder of the site K-Apps (ADR-009): files listed, opened, uploaded and
 * deleted through the API at `base`, which checks permissions, type and size.
 * The folder of a subject (ADR-010) and the folder of an offer (05/10/2026).
 */
export function FilesPanel({
  base,
  queryKey,
  canWrite,
  noFolder,
  createFolder = false,
  pathPrefix,
  showVersion = false,
  removeNote,
}: {
  /** e.g. `/subjects/<id>/files`, `/quotes/<id>/files`. */
  base: string;
  queryKey: readonly unknown[];
  canWrite: boolean;
  /** What to say while the folder does not exist yet. */
  noFolder: ReactNode;
  /** Offer «Crea la cartella vuota» (the subjects). */
  createFolder?: boolean;
  /** Where the folder is in the site, before its name: «Soggetti». */
  pathPrefix: string;
  /** The version of the offer each file was uploaded on. */
  showVersion?: boolean;
  /** Where the deletion is logged. */
  removeNote: string;
}) {
  const queryClient = useQueryClient();
  const files = useFolderFiles(base, queryKey, true);
  const [error, setError] = useState('');
  const [queue, setQueue] = useState<Upload[]>([]);
  const [dragging, setDragging] = useState(false);
  const [removing, setRemoving] = useState<SubjectFileItem | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const store = (updated: SubjectFilesResponse) => queryClient.setQueryData(queryKey, updated);

  const create = useMutation({
    mutationFn: () => apiRequest<SubjectFilesResponse>(`${base}/folder`, { method: 'POST' }),
    onSuccess: (updated) => {
      setError('');
      store(updated);
    },
    onError: (failure: Error) => setError(failure.message),
  });
  const download = useMutation({
    mutationFn: (itemId: string) => apiRequest<FileDownloadResponse>(`${base}/${itemId}/download`),
    onSuccess: (link) => {
      setError('');
      window.open(link.url, '_blank', 'noopener');
    },
    onError: (failure: Error) => setError(failure.message),
  });
  const remove = useMutation({
    mutationFn: (itemId: string) =>
      apiRequest<SubjectFilesResponse>(`${base}/${itemId}`, { method: 'DELETE' }),
    onSuccess: (updated) => {
      setRemoving(null);
      store(updated);
    },
  });

  /** One file at a time, so that the order and the messages stay clear. */
  const send = async (picked: File[]) => {
    const start = picked.map((file): Upload => {
      const refusal = uploadError(file.name, file.size);
      return { name: file.name, status: refusal ? 'refused' : 'waiting', message: refusal };
    });
    setQueue(start);
    for (const [index, file] of picked.entries()) {
      if (start[index]!.status === 'refused') continue;
      const update = (change: Partial<Upload>) =>
        setQueue((current) =>
          current.map((item, position) => (position === index ? { ...item, ...change } : item)),
        );
      update({ status: 'sending' });
      try {
        store(
          await apiUpload<SubjectFilesResponse>(
            `${base}?name=${encodeURIComponent(file.name)}`,
            file,
          ),
        );
        update({ status: 'done' });
      } catch (failure) {
        update({ status: 'refused', message: (failure as Error).message });
      }
    }
  };
  const sending = queue.some((item) => item.status === 'sending' || item.status === 'waiting');
  const data = files.data;

  return (
    <section className="card stack">
      <div className="row between">
        <h2>
          File <span className="count-pill">{data?.items.length ?? 0}</span>
        </h2>
        <div className="row">
          {data?.folder.webUrl && (
            <a className="button" href={data.folder.webUrl} target="_blank" rel="noreferrer">
              Apri in SharePoint ↗
            </a>
          )}
          {canWrite && data?.connected && (
            <button
              type="button"
              className="primary"
              disabled={sending}
              onClick={() => inputRef.current?.click()}
            >
              Carica file
            </button>
          )}
        </div>
      </div>
      {canWrite && data?.connected && (
        <>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            accept={UPLOAD_EXTENSIONS.map((extension) => `.${extension}`).join(',')}
            onChange={(event) => {
              const picked = [...(event.target.files ?? [])];
              event.target.value = '';
              if (picked.length > 0) void send(picked);
            }}
          />
          <div
            className={dragging ? 'drop-zone active' : 'drop-zone'}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              const picked = [...event.dataTransfer.files];
              if (picked.length > 0 && !sending) void send(picked);
            }}
          >
            Trascina qui i file, oppure usa «Carica file». PDF, Word, Excel, PowerPoint, immagini
            JPG e PNG · fino a 20 MB ciascuno.
          </div>
        </>
      )}
      {queue.length > 0 && (
        <ul className="upload-queue">
          {queue.map((item, index) => (
            <li key={`${item.name}-${index}`} className={item.status}>
              <strong>{item.name}</strong>{' '}
              <span className="muted small">
                {item.status === 'waiting' && 'in attesa'}
                {item.status === 'sending' && 'caricamento…'}
                {item.status === 'done' && 'caricato'}
                {item.status === 'refused' && item.message}
              </span>
            </li>
          ))}
        </ul>
      )}
      {files.isPending && <p className="muted">Caricamento…</p>}
      {files.isError && <p className="error">{files.error.message}</p>}
      {data && !data.connected && (
        <p className="muted">SharePoint non è ancora collegato in questo ambiente.</p>
      )}
      {data?.connected && !data.folder.exists && (
        <div className="stack tight">
          <p className="muted">{noFolder}</p>
          {canWrite && createFolder && (
            <div>
              <button type="button" disabled={create.isPending} onClick={() => create.mutate()}>
                {create.isPending ? 'Creazione…' : 'Crea la cartella vuota'}
              </button>
            </div>
          )}
        </div>
      )}
      {data?.folder.exists && data.folder.name && (
        <p className="muted small">
          SharePoint › K-Apps › {pathPrefix} › {data.folder.name.replaceAll('/', ' › ')}
        </p>
      )}
      {data?.folder.exists && data.items.length === 0 && (
        <p className="muted">La cartella è vuota.</p>
      )}
      {data?.folder.exists && data.items.length > 0 && (
        <div className="table-scroll">
          <table className="lines">
            <thead>
              <tr>
                <th>Nome</th>
                {showVersion && <th>Versione</th>}
                <th>Modificato</th>
                <th>Da</th>
                <th className="right">Dimensione</th>
                <th aria-label="Azioni" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.id}>
                  <td>{item.isFolder ? `📁 ${item.name}` : item.name}</td>
                  {showVersion && <td>{item.version ? `v${item.version}` : '—'}</td>}
                  <td>{item.modifiedAt ? formatTimestamp(item.modifiedAt) : '—'}</td>
                  <td>{item.modifiedBy ?? '—'}</td>
                  <td className="right">{item.size === null ? '—' : formatFileSize(item.size)}</td>
                  <td className="row-actions">
                    {!item.isFolder && (
                      <button
                        type="button"
                        className="small"
                        disabled={download.isPending}
                        onClick={() => download.mutate(item.id)}
                      >
                        Apri
                      </button>
                    )}
                    {!item.isFolder && canWrite && (
                      <button
                        type="button"
                        className="small danger"
                        onClick={() => setRemoving(item)}
                      >
                        Elimina
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {error && <p className="error">{error}</p>}
      {removing && (
        <ConfirmDialog
          title="Eliminare il file?"
          confirmLabel="Elimina"
          busy={remove.isPending}
          error={remove.error?.message}
          onConfirm={() => remove.mutate(removing.id)}
          onCancel={() => setRemoving(null)}
        >
          <p>
            «{removing.name}» va nel cestino di SharePoint, dove si può recuperare per 93 giorni.{' '}
            {removeNote}
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}

/** A file being uploaded, with what happened to it. */
interface Upload {
  name: string;
  status: 'waiting' | 'sending' | 'done' | 'refused';
  message: string | null;
}
